import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  ContentCopy as CopyIcon,
  LocalOffer as LocalOfferIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as MoneyIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import api from '../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReferralCode {
  id: number;
  code: string;
  entity_type: 'school' | 'tuition_class' | 'guardian' | 'affiliate' | 'influencer' | 'other';
  entity_id: number;
  display_name: string;
  description: string | null;
  discount_percentage: number;
  commission_percentage: number;
  max_uses: number | null;
  uses_count: number;
  max_uses_per_user: number | null;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  remaining_uses: number | null;
}

interface ReferralCodeFormData {
  code: string;
  entity_type: string;
  display_name: string;
  description: string;
  discount_percentage: string;
  commission_percentage: string;
  max_uses: string;
  max_uses_per_user: string;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
}

interface UsageRecord {
  id: number;
  user_type: string;
  user_id: number;
  user_name: string;
  exam_id: number;
  original_price: number;
  discount_amount: number;
  final_price: number;
  commission_amount: number;
  currency: string;
  status: string;
  created_at: string;
}

interface PageStats {
  total: number;
  active: number;
  total_uses: number;
  total_discount: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ENTITY_TYPE_DEFAULTS: Record<string, { discount: number; commission: number }> = {
  school: { discount: 10, commission: 10 },
  tuition_class: { discount: 10, commission: 10 },
  guardian: { discount: 5, commission: 5 },
  affiliate: { discount: 15, commission: 15 },
  influencer: { discount: 20, commission: 10 },
  other: { discount: 5, commission: 5 },
};

const ENTITY_TYPE_LABELS: Record<string, string> = {
  school: 'School',
  tuition_class: 'Tuition Class',
  guardian: 'Guardian',
  affiliate: 'Affiliate',
  influencer: 'Influencer',
  other: 'Other',
};

const ENTITY_TYPE_COLORS: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error' | 'default'> = {
  school: 'primary',
  tuition_class: 'info',
  guardian: 'success',
  affiliate: 'secondary',
  influencer: 'warning',
  other: 'default',
};

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  applied: 'warning',
  completed: 'success',
  refunded: 'error',
  cancelled: 'default',
};

const defaultFormData: ReferralCodeFormData = {
  code: '',
  entity_type: '',
  display_name: '',
  description: '',
  discount_percentage: '',
  commission_percentage: '',
  max_uses: '',
  max_uses_per_user: '',
  valid_from: '',
  valid_until: '',
  is_active: true,
};

// ─── Stat Card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => (
  <Card elevation={2} sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" variant="body2">
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold">
            {value}
          </Typography>
        </Box>
        <Box sx={{ bgcolor: `${color}.light`, color: `${color}.main`, borderRadius: 2, p: 2 }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const ReferralCodes: React.FC = () => {
  // List state
  const [codes, setCodes] = useState<ReferralCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Stats
  const [stats, setStats] = useState<PageStats>({ total: 0, active: 0, total_uses: 0, total_discount: 0 });

  // Filters
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Create/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<ReferralCode | null>(null);
  const [formData, setFormData] = useState<ReferralCodeFormData>(defaultFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Usage history modal
  const [usageModalOpen, setUsageModalOpen] = useState(false);
  const [selectedCodeForUsage, setSelectedCodeForUsage] = useState<ReferralCode | null>(null);
  const [usageHistory, setUsageHistory] = useState<UsageRecord[]>([]);
  const [usagePage, setUsagePage] = useState(0);
  const [usageTotal, setUsageTotal] = useState(0);
  const [usageStats, setUsageStats] = useState<any>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  // Deactivate dialog
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [codeToDeactivate, setCodeToDeactivate] = useState<ReferralCode | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadCodes = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params: any = { page: page + 1, limit: rowsPerPage };
      if (entityTypeFilter) params.entity_type = entityTypeFilter;
      if (statusFilter !== '') params.is_active = statusFilter;
      if (searchQuery) params.search = searchQuery;

      const response = await api.referralCodes.getAll(params);
      const data = response.data;
      setCodes(data.referral_codes || data.codes || []);
      setTotal(data.pagination?.total || data.total || 0);

      if (data.stats) {
        setStats(data.stats);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load referral codes');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, entityTypeFilter, statusFilter, searchQuery]);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  // Derive stats from loaded codes when backend doesn't return them
  useEffect(() => {
    if (codes.length > 0 && !stats.total) {
      setStats({
        total: total,
        active: codes.filter((c) => c.is_active).length,
        total_uses: codes.reduce((sum, c) => sum + (c.uses_count || 0), 0),
        total_discount: 0,
      });
    }
  }, [codes, total, stats.total]);

  // ── Form helpers ─────────────────────────────────────────────────────────────

  const openCreateModal = () => {
    setEditingCode(null);
    setFormData(defaultFormData);
    setFormErrors({});
    setModalOpen(true);
  };

  const openEditModal = (code: ReferralCode) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      entity_type: code.entity_type,
      display_name: code.display_name,
      description: code.description || '',
      discount_percentage: String(code.discount_percentage),
      commission_percentage: String(code.commission_percentage),
      max_uses: code.max_uses != null ? String(code.max_uses) : '',
      max_uses_per_user: code.max_uses_per_user != null ? String(code.max_uses_per_user) : '',
      valid_from: code.valid_from ? code.valid_from.split('T')[0] : '',
      valid_until: code.valid_until ? code.valid_until.split('T')[0] : '',
      is_active: code.is_active,
    });
    setFormErrors({});
    setModalOpen(true);
  };

  const handleFormChange = (field: keyof ReferralCodeFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleEntityTypeChange = (value: string) => {
    const defaults = ENTITY_TYPE_DEFAULTS[value] || { discount: 0, commission: 0 };
    setFormData((prev) => ({
      ...prev,
      entity_type: value,
      discount_percentage: String(defaults.discount),
      commission_percentage: String(defaults.commission),
    }));
    setFormErrors((prev) => ({ ...prev, entity_type: '' }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.code.trim()) errors.code = 'Code is required';
    if (!formData.entity_type) errors.entity_type = 'Entity type is required';
    if (!formData.display_name.trim()) errors.display_name = 'Display name is required';
    const discount = parseFloat(formData.discount_percentage);
    if (isNaN(discount) || discount < 0 || discount > 100)
      errors.discount_percentage = 'Must be 0–100';
    const commission = parseFloat(formData.commission_percentage);
    if (isNaN(commission) || commission < 0 || commission > 100)
      errors.commission_percentage = 'Must be 0–100';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      const payload: any = {
        code: formData.code.trim().toUpperCase(),
        entity_type: formData.entity_type,
        display_name: formData.display_name.trim(),
        description: formData.description.trim() || null,
        discount_percentage: parseFloat(formData.discount_percentage),
        commission_percentage: parseFloat(formData.commission_percentage),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        max_uses_per_user: formData.max_uses_per_user ? parseInt(formData.max_uses_per_user) : null,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null,
      };

      if (editingCode) {
        payload.is_active = formData.is_active;
        await api.referralCodes.update(editingCode.id, payload);
        setSuccessMsg('Referral code updated successfully');
      } else {
        await api.referralCodes.create(payload);
        setSuccessMsg('Referral code created successfully');
      }

      setModalOpen(false);
      loadCodes();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save referral code');
    } finally {
      setSaving(false);
    }
  };

  // ── Usage history ────────────────────────────────────────────────────────────

  const openUsageModal = async (code: ReferralCode) => {
    setSelectedCodeForUsage(code);
    setUsagePage(0);
    setUsageModalOpen(true);
    await loadUsageHistory(code.id, 0);
  };

  const loadUsageHistory = async (codeId: number, pg: number) => {
    try {
      setUsageLoading(true);
      const [usageRes, statsRes] = await Promise.all([
        api.referralCodes.getUsage(codeId, { page: pg + 1, limit: 10 }),
        api.referralCodes.getStats(codeId),
      ]);
      setUsageHistory(usageRes.data.usages || usageRes.data.usage || []);
      setUsageTotal(usageRes.data.pagination?.total || 0);
      setUsageStats(statsRes.data);
    } catch {
      setUsageHistory([]);
    } finally {
      setUsageLoading(false);
    }
  };

  const handleUsagePageChange = async (_: unknown, newPage: number) => {
    setUsagePage(newPage);
    if (selectedCodeForUsage) {
      await loadUsageHistory(selectedCodeForUsage.id, newPage);
    }
  };

  // ── Deactivate ───────────────────────────────────────────────────────────────

  const confirmDeactivate = (code: ReferralCode) => {
    setCodeToDeactivate(code);
    setDeactivateDialogOpen(true);
  };

  const handleDeactivate = async () => {
    if (!codeToDeactivate) return;
    try {
      setDeactivating(true);
      await api.referralCodes.deactivate(codeToDeactivate.id);
      setSuccessMsg(`"${codeToDeactivate.code}" has been deactivated`);
      setDeactivateDialogOpen(false);
      loadCodes();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to deactivate');
    } finally {
      setDeactivating(false);
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setSuccessMsg(`"${code}" copied to clipboard`);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatUses = (code: ReferralCode) => {
    const max = code.max_uses != null ? String(code.max_uses) : '∞';
    return `${code.uses_count} / ${max}`;
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Referral Codes
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateModal}>
          Add Code
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Codes"
            value={total}
            icon={<LocalOfferIcon />}
            color="primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Active Codes"
            value={stats.active}
            icon={<CheckCircleIcon />}
            color="success"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Uses"
            value={stats.total_uses}
            icon={<BarChartIcon />}
            color="info"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Discount Given"
            value={stats.total_discount ? `₹${Number(stats.total_discount).toLocaleString('en-IN')}` : '₹0'}
            icon={<MoneyIcon />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Entity Type</InputLabel>
            <Select
              value={entityTypeFilter}
              label="Entity Type"
              onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All Types</MenuItem>
              {Object.entries(ENTITY_TYPE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Search code or name…"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            sx={{ minWidth: 220 }}
          />
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="center">Discount</TableCell>
              <TableCell align="center">Commission</TableCell>
              <TableCell align="center">Uses</TableCell>
              <TableCell>Valid Until</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : codes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">No referral codes found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              codes.map((code) => (
                <TableRow key={code.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontWeight="bold"
                        sx={{ letterSpacing: 1 }}
                      >
                        {code.code}
                      </Typography>
                      <Tooltip title="Copy code">
                        <IconButton size="small" onClick={() => copyCode(code.code)}>
                          <CopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>{code.display_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={ENTITY_TYPE_LABELS[code.entity_type] || code.entity_type}
                      color={ENTITY_TYPE_COLORS[code.entity_type] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">{code.discount_percentage}%</TableCell>
                  <TableCell align="center">{code.commission_percentage}%</TableCell>
                  <TableCell align="center">{formatUses(code)}</TableCell>
                  <TableCell>{formatDate(code.valid_until)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={code.is_active ? 'Active' : 'Inactive'}
                      color={code.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openEditModal(code)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Usage History">
                        <IconButton size="small" color="info" onClick={() => openUsageModal(code)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {code.is_active && (
                        <Tooltip title="Deactivate">
                          <IconButton size="small" color="error" onClick={() => confirmDeactivate(code)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </TableContainer>

      {/* ── Create / Edit Modal ─────────────────────────────────────────────── */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCode ? `Edit — ${editingCode.code}` : 'Create Referral Code'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {/* Code */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Code"
                fullWidth
                required
                disabled={!!editingCode}
                value={formData.code}
                onChange={(e) => handleFormChange('code', e.target.value.toUpperCase().replace(/\s/g, ''))}
                error={!!formErrors.code}
                helperText={formErrors.code || (editingCode ? 'Code cannot be changed' : 'Auto-uppercased')}
                inputProps={{ style: { fontFamily: 'monospace', letterSpacing: 2 } }}
              />
            </Grid>

            {/* Entity Type */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required error={!!formErrors.entity_type}>
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={formData.entity_type}
                  label="Entity Type"
                  onChange={(e) => handleEntityTypeChange(e.target.value)}
                >
                  {Object.entries(ENTITY_TYPE_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
                {formErrors.entity_type && (
                  <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                    {formErrors.entity_type}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Display Name */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Display Name"
                fullWidth
                required
                value={formData.display_name}
                onChange={(e) => handleFormChange('display_name', e.target.value)}
                error={!!formErrors.display_name}
                helperText={formErrors.display_name || 'Shown to users when code is applied'}
              />
            </Grid>

            {/* Description */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                helperText="Optional internal note"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>

            {/* Discount % */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Discount %"
                fullWidth
                required
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                value={formData.discount_percentage}
                onChange={(e) => handleFormChange('discount_percentage', e.target.value)}
                error={!!formErrors.discount_percentage}
                helperText={formErrors.discount_percentage || 'Discount given to user (0–100)'}
              />
            </Grid>

            {/* Commission % */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Commission %"
                fullWidth
                required
                type="number"
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                value={formData.commission_percentage}
                onChange={(e) => handleFormChange('commission_percentage', e.target.value)}
                error={!!formErrors.commission_percentage}
                helperText={formErrors.commission_percentage || 'Commission owed to referrer (0–100)'}
              />
            </Grid>

            {/* Max Uses */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Max Uses"
                fullWidth
                type="number"
                inputProps={{ min: 1 }}
                value={formData.max_uses}
                onChange={(e) => handleFormChange('max_uses', e.target.value)}
                helperText="Leave blank for unlimited"
              />
            </Grid>

            {/* Max Uses Per User */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Max Uses Per User"
                fullWidth
                type="number"
                inputProps={{ min: 1 }}
                value={formData.max_uses_per_user}
                onChange={(e) => handleFormChange('max_uses_per_user', e.target.value)}
                helperText="Leave blank for unlimited"
              />
            </Grid>

            {/* Valid From */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Valid From"
                fullWidth
                type="date"
                value={formData.valid_from}
                onChange={(e) => handleFormChange('valid_from', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Optional"
              />
            </Grid>

            {/* Valid Until */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Valid Until"
                fullWidth
                type="date"
                value={formData.valid_until}
                onChange={(e) => handleFormChange('valid_until', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Leave blank for no expiry"
              />
            </Grid>

            {/* Is Active (edit mode only) */}
            {editingCode && (
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => handleFormChange('is_active', e.target.checked)}
                      color="success"
                    />
                  }
                  label={formData.is_active ? 'Active' : 'Inactive'}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editingCode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Usage History Modal ─────────────────────────────────────────────── */}
      <Dialog
        open={usageModalOpen}
        onClose={() => setUsageModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Usage History — {selectedCodeForUsage?.code}</DialogTitle>
        <DialogContent dividers>
          {/* Usage stats */}
          {usageStats && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {[
                { label: 'Total Uses', value: usageStats.total_uses ?? usageStats.uses_count ?? 0 },
                { label: 'Completed', value: usageStats.completed_uses ?? 0 },
                { label: 'Total Discount', value: `₹${Number(usageStats.total_discount_amount ?? 0).toLocaleString('en-IN')}` },
                { label: 'Total Commission', value: `₹${Number(usageStats.total_commission_amount ?? 0).toLocaleString('en-IN')}` },
              ].map((s) => (
                <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
                  <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary">{s.label}</Typography>
                    <Typography variant="h6" fontWeight="bold">{s.value}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}

          {usageLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : usageHistory.length === 0 ? (
            <Typography align="center" color="textSecondary" sx={{ py: 3 }}>
              No usage records yet
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Original</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Final</TableCell>
                    <TableCell align="right">Commission</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usageHistory.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Typography variant="body2">{u.user_name || `${u.user_type} #${u.user_id}`}</Typography>
                      </TableCell>
                      <TableCell align="right">₹{Number(u.original_price).toLocaleString('en-IN')}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        -₹{Number(u.discount_amount).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        ₹{Number(u.final_price).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'warning.main' }}>
                        ₹{Number(u.commission_amount).toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={u.status}
                          color={STATUS_COLORS[u.status] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(u.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={usageTotal}
                page={usagePage}
                onPageChange={handleUsagePageChange}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
              />
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsageModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Deactivate Confirmation ─────────────────────────────────────────── */}
      <Dialog open={deactivateDialogOpen} onClose={() => setDeactivateDialogOpen(false)}>
        <DialogTitle>Deactivate Referral Code</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate{' '}
            <strong>{codeToDeactivate?.code}</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Users will no longer be able to apply this code. Existing usages will not be affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialogOpen(false)} disabled={deactivating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeactivate}
            disabled={deactivating}
          >
            {deactivating ? 'Deactivating…' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReferralCodes;
